import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
	ARCHIVE_STATUS_LABELS,
	COST_STATUS_LABELS,
	OCR_STATUS_LABELS,
} from '@/tms/gbox-scans/_models/gbox-scan-status.labels';
import { ArchiveStatus, CostStatus, OcrStatus } from '@/tms/gbox-scans/_models/gbox-scans.model';

interface BadgeMeta {
	readonly label: string;
	readonly icon: string;
	readonly tooltip: string;
	readonly modifier: string;
}

const OCR_BADGE_META: Readonly<Record<OcrStatus, BadgeMeta>> = {
	pending: {
		label: OCR_STATUS_LABELS.pending,
		icon: 'fa-clock',
		tooltip: 'Oczekuje na analizę OCR',
		modifier: 'ocr-badge--pending',
	},
	processing: {
		label: OCR_STATUS_LABELS.processing,
		icon: 'fa-spinner fa-spin',
		tooltip: 'OCR w trakcie analizy',
		modifier: 'ocr-badge--processing',
	},
	completed: {
		label: OCR_STATUS_LABELS.completed,
		icon: 'fa-wand-magic-sparkles',
		tooltip: 'Przeanalizowane przez AI',
		modifier: 'ocr-badge--completed',
	},
	failed: {
		label: OCR_STATUS_LABELS.failed,
		icon: 'fa-triangle-exclamation',
		tooltip: 'Błąd OCR',
		modifier: 'ocr-badge--failed',
	},
	skipped: {
		label: OCR_STATUS_LABELS.skipped,
		icon: 'fa-circle-minus',
		tooltip: 'Pominięte (nieobsługiwany typ pliku)',
		modifier: 'ocr-badge--skipped',
	},
};

const COST_BADGE_META: Readonly<Record<CostStatus, BadgeMeta>> = {
	pending: {
		label: COST_STATUS_LABELS.pending,
		icon: 'fa-clock',
		tooltip: 'Wymaga weryfikacji',
		modifier: 'cost-badge--pending',
	},
	confirmed: {
		label: COST_STATUS_LABELS.confirmed,
		icon: 'fa-circle-check',
		tooltip: 'Koszt zatwierdzony',
		modifier: 'cost-badge--confirmed',
	},
	rejected: {
		label: COST_STATUS_LABELS.rejected,
		icon: 'fa-circle-xmark',
		tooltip: 'Koszt odrzucony',
		modifier: 'cost-badge--rejected',
	},
	skipped: {
		label: COST_STATUS_LABELS.skipped,
		icon: 'fa-circle-minus',
		tooltip: 'Pominięto — nie jest to dokument kosztowy',
		modifier: 'cost-badge--skipped',
	},
};

const ARCHIVE_BADGE_META: Readonly<Record<ArchiveStatus, BadgeMeta>> = {
	pending: {
		label: ARCHIVE_STATUS_LABELS.pending,
		icon: 'fa-clock',
		tooltip: 'Oczekuje na zapis w S3',
		modifier: 'archive-badge--pending',
	},
	archived: {
		label: ARCHIVE_STATUS_LABELS.archived,
		icon: 'fa-circle-check',
		tooltip: 'Skan zapisany w S3',
		modifier: 'archive-badge--archived',
	},
	failed: {
		label: ARCHIVE_STATUS_LABELS.failed,
		icon: 'fa-triangle-exclamation',
		tooltip: 'Nie udało się zapisać skanu w S3',
		modifier: 'archive-badge--failed',
	},
};

@Component({
	selector: 'app-scan-status-badges',
	standalone: true,
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [MatIconModule, MatTooltipModule],
	templateUrl: './scan-status-badges.component.html',
	styleUrl: './scan-status-badges.component.scss',
})
export class ScanStatusBadgesComponent {
	readonly ocrStatus = input.required<OcrStatus>();
	readonly costStatus = input.required<CostStatus>();
	readonly archiveStatus = input.required<ArchiveStatus>();
	readonly showOcr = input(true);
	readonly showArchive = input(true);

	readonly ocrMeta = computed<BadgeMeta>(() => OCR_BADGE_META[this.ocrStatus()]);
	readonly costMeta = computed<BadgeMeta>(() => COST_BADGE_META[this.costStatus()]);
	readonly archiveMeta = computed<BadgeMeta>(() => ARCHIVE_BADGE_META[this.archiveStatus()]);
}
